import type {
  AxiosError,
  AxiosProgressEvent,
  AxiosRequestConfig,
  InternalAxiosRequestConfig
} from 'axios'
import axios from 'axios'
import { isString } from 'lodash-es'
import { ResponseCode, type Response } from '@/types/http'
import { aesDecrypt, getApiHeaderProtect } from './aes'
import { encryptPayloadBinary } from './cryptoPayload'
import { httpApiToFileInterceptor } from './httpApiToFileInterceptor'
import { getDeviceFingerprintInfo } from './fingerprint'
import { clearToken } from '@/composables/useToken'
import { localeToLegacyCode } from './localeMapping'
import type { LangCode } from '@/types/config'
import CryptoJS from 'crypto-js'

// 不加密的接口白名单
const whiteList: string[] = [
  '/app-api/infra/config/list',
  '/app-api/system/lang-data/release-list',
  '/app-api/system/theme/sub-list',
  '/captcha/challenge',
  '/captcha/verify',
  'https://captcha.vipgtest.com/captcha/challenge',
  'https://captcha.vipgtest.com/captcha/verify'
]

const onHandleUploadProgress = (progressEvent: AxiosProgressEvent) => {
  if (progressEvent.lengthComputable && progressEvent.total) {
    return Math.round((progressEvent.loaded / progressEvent.total) * 10000) / 100.0
  }
  return 0
}

// 当前环境是否开启加密
export const isCryptoEnv = import.meta.env.VITE_CURRENT_ENV === 'prod'

export interface RequestOptions extends AxiosRequestConfig {
  body?: Record<string, any> | FormData | Uint8Array | Blob | File
  // 调用接口时，弹出全局loading toast
  showLoading?: boolean
  // 接口返回错误信息了 不toast提示
  noMsg?: boolean
  // 错误提示  toast或者弹窗
  msgType?: 'simpleToast' | 'toast' | 'dialog'
  // 需要登录 未登录跳登录页
  needLogin?: boolean
  // 上传进度
  onProgress?: (_e: number) => void
  // 手动延迟
  delay?: number
}

const axiosInstance = axios.create({
  // 优先读取 .env 的 VITE_API_URL，未配置时回退到现有策略
  baseURL: import.meta.env.VITE_CURRENT_ENV === 'prod' ? import.meta.env.VITE_API_URL : '',
  timeout: 60000
})

// 请求拦截器
axiosInstance.interceptors.request.use(
  async (config: InternalAxiosRequestConfig<RequestOptions>) => {
    // 添加 protect 请求头
    config.headers.protect = getApiHeaderProtect(config.url)
    return config
  },
  (error: AxiosError) => {
    console.error('Request error:', error)
    return Promise.reject(error)
  }
)

export const http = async <R = Response<any>>(
  url: string,
  opts: RequestOptions = {
    body: {},
    onProgress: () => {}
  }
): Promise<R> => {
  // API请求文件化拦截处理
  const interceptedResponse = await httpApiToFileInterceptor(url, opts)
  if (interceptedResponse) {
    return interceptedResponse
  }

  if (!getDeviceFingerprintInfo()) {
    await getDeviceFingerprintInfo()
  }

  let requestData = opts.data || opts.body || {}
  const isCrypto =
    opts.method?.toUpperCase() === 'POST' &&
    !whiteList.includes(url) &&
    !(requestData instanceof FormData) &&
    isCryptoEnv

  const defaultHeaders: Record<string, any> = {
    Authorization: `Bearer ${localStorage.getItem('AUTH_TOKEN') || ''}`,
    'X-client-request-time': Date.now().toString(),
    'DEVICE-TYPE': 'pc',
    'DEVICE-CODE': getDeviceFingerprintInfo()?.id || '',
    Timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    Terminal: 'pc',
    LANG: localeToLegacyCode((localStorage.getItem('CURRENT_LANG') || 'en-US') as LangCode),
    TraceId: CryptoJS.SHA1(Math.random().toString(36)).toString(CryptoJS.enc.Hex),
    ...opts.headers
  }

  if (!(requestData instanceof FormData)) {
    defaultHeaders['Content-Type'] = isCrypto ? 'application/octet-stream' : 'application/json'
  }

  if (isCrypto) {
    defaultHeaders.enc = 'true'
    requestData = encryptPayloadBinary(requestData)
  }

  const response = await axiosInstance.request<R>({
    url,
    signal: opts.signal,
    method: opts.method || 'post',
    headers: defaultHeaders,
    data: requestData,
    params: opts.method?.toLowerCase() === 'get' ? opts.params || opts.body : {},
    onUploadProgress: (progressEvent: AxiosProgressEvent) => {
      if (opts.onProgress) {
        opts.onProgress(onHandleUploadProgress(progressEvent))
      }
    }
  })

  if (opts.delay) {
    await new Promise<void>((resolve) => setTimeout(() => resolve(), opts.delay))
  }

  let responseData = response.data

  if (isCrypto) {
    try {
      if (isString(responseData)) {
        responseData = JSON.parse(aesDecrypt(responseData, 'Ot8Tl+jXfd9DT2AvDvlNSQ=='))
        console.log('解密成功', url, opts.body, responseData)
      }
    } catch (error) {
      console.error('解密失败', url, error, responseData)
    }
  }

  const { msg, code } = responseData as Response
  switch (code) {
    case ResponseCode.success:
      return responseData
    // captcha 接口返回 0 作为成功状态
    case 0:
      return responseData
    case ResponseCode.unAuth: {
      // localStorage.removeItem('AUTH_TOKEN')
      clearToken()
      throw new Error('Unauthorized')
    }
    case ResponseCode.denyIp:
      throw new Error('denyIp')
    case ResponseCode.maintain: {
      throw new Error('maintain')
    }
    default: {
      return Promise.reject(responseData || { msg: msg || '网络错误' })
    }
  }
}
